import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/api';

interface LoginFormProps {
  onSwitchToRegister: () => void;
}

type LoginMode = 'email' | 'phone';

const LoginForm: React.FC<LoginFormProps> = ({ onSwitchToRegister }) => {
  const { login } = useAuth();
  const [loginMode, setLoginMode] = useState<LoginMode>('email');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    phone: '',
    otp: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpMessage, setOtpMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('LoginForm: Attempting email login for:', formData.email);
      await login(formData.email, formData.password);
      console.log('LoginForm: Login successful');
    } catch (error: any) {
      console.error('LoginForm: Login error:', error.message);
      
      if (error.message.includes('Invalid email or password')) {
        setError('❌ Invalid email or password. Please check your credentials and try again.');
      } else if (error.message.includes('Too many requests')) {
        setError('🚫 Too many login attempts. Please wait a few minutes before trying again.');
      } else if (error.message.includes('Network Error')) {
        setError('🌐 Network error. Please check your connection and try again.');
      } else {
        setError(`❌ Login failed: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOtp = async () => {
    if (!formData.phone) {
      setError('Please enter your phone number');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await apiService.requestOtp(formData.phone);
      if (response.success) {
        setOtpSent(true);
        setOtpMessage('✅ OTP sent to your phone number');
        // In development, show the debug OTP
        if (response.debug_otp) {
          setOtpMessage(`✅ OTP sent! (Dev mode OTP: ${response.debug_otp})`);
        }
      } else {
        setError('Failed to send OTP. Please try again.');
      }
    } catch (error: any) {
      console.error('OTP request error:', error);
      setError('❌ Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.phone || !formData.otp) {
      setError('Please enter phone number and OTP');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await apiService.verifyOtp(formData.phone, formData.otp);
      if (response.success && response.token) {
        // Store token and user data
        localStorage.setItem('token', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        // Reload to reinitialize auth
        window.location.reload();
      } else {
        setError('❌ Invalid OTP. Please try again.');
      }
    } catch (error: any) {
      console.error('OTP verification error:', error);
      if (error.response?.data?.attemptsRemaining) {
        setError(`❌ Invalid OTP. ${error.response.data.attemptsRemaining} attempts remaining.`);
      } else {
        setError('❌ OTP verification failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    marginBottom: 0,
    backgroundColor: loading ? '#f9fafb' : 'white'
  };

  const labelStyle = { 
    display: 'block', 
    marginBottom: '0.5rem', 
    fontSize: '14px', 
    fontWeight: 500,
    color: '#374151'
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-primary">
      <div className="card" style={{ maxWidth: '420px', width: '100%' }}>
        <div className="text-center mb-8">
          <h1 className="logo" style={{ fontSize: '2rem' }}>Yojana</h1>
          <p className="text-gray mt-2">Sign in to your account</p>
        </div>

        {/* Login Mode Toggle */}
        <div style={{ 
          display: 'flex', 
          marginBottom: '1.5rem',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          overflow: 'hidden'
        }}>
          <button
            type="button"
            onClick={() => { setLoginMode('email'); setError(''); setOtpSent(false); }}
            style={{
              flex: 1,
              padding: '10px 16px',
              border: 'none',
              backgroundColor: loginMode === 'email' ? 'var(--primary-color, #667eea)' : 'white',
              color: loginMode === 'email' ? 'white' : '#374151',
              fontWeight: 500,
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            📧 Email
          </button>
          <button
            type="button"
            onClick={() => { setLoginMode('phone'); setError(''); setOtpSent(false); }}
            style={{
              flex: 1,
              padding: '10px 16px',
              border: 'none',
              borderLeft: '1px solid #e5e7eb',
              backgroundColor: loginMode === 'phone' ? 'var(--primary-color, #667eea)' : 'white',
              color: loginMode === 'phone' ? 'white' : '#374151',
              fontWeight: 500,
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            📱 Phone
          </button>
        </div>

        {error && (
          <div className="alert alert-danger" style={{ 
            marginBottom: '1rem',
            padding: '12px 16px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            color: '#dc2626',
            fontSize: '14px',
            lineHeight: '1.5'
          }}>
            {error}
          </div>
        )}

        {otpMessage && !error && (
          <div style={{ 
            marginBottom: '1rem',
            padding: '12px 16px',
            backgroundColor: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: '8px',
            color: '#16a34a',
            fontSize: '14px',
            lineHeight: '1.5'
          }}>
            {otpMessage}
          </div>
        )}

        {/* Email Login Form */}
        {loginMode === 'email' && (
          <form onSubmit={handleEmailLogin} data-testid="email-login-form">
            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Email Address</label>
              <input
                name="email"
                type="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
                className="input"
                disabled={loading}
                required
                style={inputStyle}
                data-testid="email-input"
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={labelStyle}>Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  className="input"
                  disabled={loading}
                  required
                  style={{ ...inputStyle, paddingRight: '40px' }}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    fontSize: '1.2rem',
                    color: '#6b7280'
                  }}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary"
              style={{
                opacity: loading ? 0.7 : 1,
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <div className="loading">
                  <div className="spinner"></div>
                  Signing in...
                </div>
              ) : (
                'Sign In'
              )}
            </button>

            {/* Forgot Password Link */}
            <div style={{ textAlign: 'center', marginTop: '16px' }}>
              <a
                href="/forgot-password"
                style={{
                  color: 'var(--primary-color, #667eea)',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500
                }}
                onClick={(e) => {
                  e.preventDefault();
                  window.history.pushState({}, '', '/forgot-password');
                  window.location.reload();
                }}
              >
                Forgot your password?
              </a>
            </div>
          </form>
        )}

        {/* Phone OTP Login Form */}
        {loginMode === 'phone' && (
          <form onSubmit={handleVerifyOtp} data-testid="phone-login-form">
            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Phone Number</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  name="phone"
                  type="tel"
                  placeholder="+91 1234567890"
                  value={formData.phone}
                  onChange={handleChange}
                  className="input"
                  disabled={loading || otpSent}
                  required
                  style={{ ...inputStyle, flex: 1 }}
                  data-testid="phone-input"
                />
                {!otpSent && (
                  <button
                    type="button"
                    onClick={handleRequestOtp}
                    disabled={loading || !formData.phone}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: 'var(--primary-color, #667eea)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: 500,
                      fontSize: '14px',
                      cursor: loading || !formData.phone ? 'not-allowed' : 'pointer',
                      opacity: loading || !formData.phone ? 0.6 : 1,
                      whiteSpace: 'nowrap'
                    }}
                    data-testid="request-otp-btn"
                  >
                    {loading ? '...' : 'Get OTP'}
                  </button>
                )}
              </div>
            </div>

            {otpSent && (
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={labelStyle}>Enter OTP</label>
                <input
                  name="otp"
                  type="text"
                  placeholder="Enter 6-digit OTP"
                  value={formData.otp}
                  onChange={handleChange}
                  className="input"
                  disabled={loading}
                  required
                  maxLength={6}
                  style={{ 
                    ...inputStyle, 
                    letterSpacing: '8px',
                    textAlign: 'center',
                    fontSize: '20px',
                    fontWeight: 600
                  }}
                  data-testid="otp-input"
                />
                <p style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
                  Didn't receive OTP?{' '}
                  <button
                    type="button"
                    onClick={() => { setOtpSent(false); setOtpMessage(''); }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--primary-color, #667eea)',
                      cursor: 'pointer',
                      textDecoration: 'underline'
                    }}
                  >
                    Resend
                  </button>
                </p>
              </div>
            )}

            {otpSent && (
              <button
                type="submit"
                disabled={loading || formData.otp.length !== 6}
                className="btn btn-primary"
                style={{
                  opacity: loading || formData.otp.length !== 6 ? 0.7 : 1,
                  cursor: loading || formData.otp.length !== 6 ? 'not-allowed' : 'pointer'
                }}
                data-testid="verify-otp-btn"
              >
                {loading ? (
                  <div className="loading">
                    <div className="spinner"></div>
                    Verifying...
                  </div>
                ) : (
                  'Verify & Sign In'
                )}
              </button>
            )}
          </form>
        )}

        <div className="text-center" style={{ marginTop: '1.5rem' }}>
          <p className="text-gray">
            Don't have an account?{' '}
            <button onClick={onSwitchToRegister} className="btn-link" data-testid="switch-to-register">
              Sign up here
            </button>
          </p>
        </div>

        {/* Debug info for development */}
        {process.env.NODE_ENV === 'development' && (
          <div style={{
            marginTop: '1rem',
            padding: '8px',
            backgroundColor: '#f0f9ff',
            border: '1px solid #bae6fd',
            borderRadius: '4px',
            fontSize: '12px',
            color: '#0369a1'
          }}>
            <strong>Dev Info:</strong> API URL: {process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL || 'http://localhost:8001'}
            {loginMode === 'phone' && <span> | Mock OTP: 121212</span>}
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginForm;
